import { HttpError, useList, useOne } from "@refinedev/core";
import Select from "components/select";
import { IEventGroup, IGroupNameIdsItem } from "interfaces";
import { useEffect, useState } from "react";
import { EventGroupsTable } from "./table";
import { EventGroupsCard } from "./card";
import Pagination from "components/pagination";
import { DataCard } from "./dataCard";
import { CreditCardIcon, UserIcon, UsersIcon } from "@heroicons/react/solid";
import { get } from "lodash";

const ALL_OPTION = {value: "", label: "All"};

const GROUP_STATUS = [
  {label: "All", value: ""},
  {label: "Opening", value: "1"},
  {label: "Send payment", value: "2"},
  {label: "Closed", value: "3"},
  {label: "Success", value: "4"},
  {label: "Canceled", value: "0"},
];

export const Groups = ({currentFilterValues, isTrigger, currency}: {currentFilterValues: any, isTrigger: boolean, currency?: string}) => {
  const [groupFilters, setGroupFilters] = useState({
    group_name: "",
    group_status: "",
    page: "1"
  });

  const {
    data,
    refetch,
    isLoading
  } = useList<IEventGroup>({
    resource: `dashboard/event/${currentFilterValues.event_id}/group-ticket`,
    pagination: {
      mode: "off"
    },
    meta: {
      resource: "data",
      filters: [
        { field: "group_name", operator: "eq", value: groupFilters.group_name },
        { field: "group_status", operator: "eq", value: groupFilters.group_status },
        { field: "from_date", operator: "eq", value: currentFilterValues.from_date },
        { field: "to_date", operator: "eq", value: currentFilterValues.to_date },
        { field: "page", operator: "eq", value: groupFilters.page },
      ],
    },
    queryOptions: {
      enabled: false
    } as any
  });

  const _handleFilterChange = (key: string, value: any) => {
    const updatedFilter = {
      [key]: value,
      ...(key !== "page" && {page: "1"})
    }
    setGroupFilters({
      ...groupFilters,
      ...updatedFilter
    })
  }

  useEffect(() => {
    if (isTrigger) {
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFilterValues, groupFilters, isTrigger]);

  useEffect(() => {
    setGroupFilters({
      group_name: "",
      group_status: "",
      page: "1"
    })
  }, [currentFilterValues]) // reset state on filter props change

  const { data: groupNameIdsData, isLoading: isLoadingGroupNameIds } = useOne<IGroupNameIdsItem[], HttpError>({
    resource: "dashboard/event",
    id: `${currentFilterValues?.event_id}/group-names`,
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!(currentFilterValues.event_id && isTrigger),
    } as any,
  });

  const groupNamesIds = ((groupNameIdsData?.data ?? []).length > 0 ? [ALL_OPTION] : []).concat((groupNameIdsData?.data ?? []).map((group: IGroupNameIdsItem) => (
    { value: group.name_en, label: group.name_en }
  )));

  const tableQueryResult = data;

  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <div className="flex-1">
          <DataCard
            name={"Groups"}
            icon={{
              svg: UsersIcon,
              background: "#DEF6FF",
              fill: "#0162D1"
            }}
            value={get(tableQueryResult, "total_group", 0)}
          />
        </div>
        <div className="flex-1">
          <DataCard
            name={"Paid groups"}
            icon={{
              svg: CreditCardIcon,
              background: "#DEF6FF",
              fill: "#0162D1"
            }}
            value={get(tableQueryResult, "total_paid_group", 0)}
          />
        </div>
        <div className="flex-1">
          <DataCard
            name={"Members"}
            icon={{
              svg: UserIcon,
              background: "#DDFDCD",
              fill: "#16831C"
            }}
            value={get(tableQueryResult, "total_member", 0)}
          />
        </div>
        <div className="flex-1">
          <DataCard
            name={"Paid members"}
            icon={{
              svg: CreditCardIcon,
              background: "#DDFDCD",
              fill: "#16831C"
            }}
            value={get(tableQueryResult, "total_paid_member", 0)}
          />
        </div>
      </div>
      <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-y-4 gap-x-2 mb-4">
        <div className="col-span-1">
          <label htmlFor="group-name" className="block text-sm font-medium leading-6">
            Group name
          </label>
          <div className="mt-2">
            <Select 
              key={`group-name__${currentFilterValues.event_id}`}
              options={groupNamesIds}
              id="group-name"
              isLoading={isLoadingGroupNameIds}
              placeholder="Select Group"
              className="py-0.5 w-full sm:max-w-full !rounded-md"
              value={groupNamesIds.find((selected: {label: string; value: string}) => selected.value === (groupFilters.group_name)) || ""}
              onChange={(selected: {label: string; value: string}) => _handleFilterChange("group_name", selected.value)}      
            />
          </div>
        </div>
        <div className="col-span-1">
          <label htmlFor="group-status" className="block text-sm font-medium leading-6">
            Group status
          </label>
          <div className="mt-2">
            <Select 
              key={`group-status__${currentFilterValues.event_id}`}
              options={GROUP_STATUS}
              id="event-phase"
              placeholder="Select Phase"
              className="py-0.5 w-full sm:max-w-full !rounded-md"
              value={GROUP_STATUS.find((selected: {label: string; value: string}) => selected.value === (groupFilters.group_status)) || ""}
              onChange={(selected: {label: string; value: string}) => _handleFilterChange("group_status", selected.value)}      
            />
          </div>
        </div>
      </div>
      <div className="sm:hidden">
        <EventGroupsCard 
          data={tableQueryResult?.data}
          pageSize={tableQueryResult?.pageSize}
          currentPage={tableQueryResult?.paging.current_page}
          isLoading={isLoading}
          currency={currency}
        />
      </div>
      <div className="hidden sm:block">
        <EventGroupsTable
          data={tableQueryResult?.data}
          pageSize={tableQueryResult?.pageSize}
          currentPage={tableQueryResult?.paging.current_page}
          isLoading={isLoading}
          currency={currency}
        />
      </div>
      {tableQueryResult?.paging  && tableQueryResult.paging.total_page > 1 ? (
        <div className="mt-6">
          <Pagination 
            hideTotal
            currentPage={tableQueryResult?.paging.current_page}
            totalPage={tableQueryResult?.paging.total_page}
            total={{
              totalItems: tableQueryResult?.paging.total_item || 0,
              total: tableQueryResult?.paging.total
            }}
            onChange={(page: number) => _handleFilterChange("page", page)}
          />
        </div>
      ) : <></>}
    </div>

  )
}